// src/inventory-slots/inventorySlotsApi.ts
import { apiClient } from "@maat/api-base";
var slotPath = (kind) => `${kind}s`;
var inventorySlotsApi = {
  /**
   * `calendar.view`. The grid for one inventory over a window. `from`/`to` are
   * BOTH required and at most `MAX_SLOT_WINDOW_DAYS` apart — bound the picker's
   * range against that constant rather than discovering it as a 400.
   */
  listSlots: async (kind, inventoryId, params) => {
    return apiClient.get(`/main/api/${slotPath(kind)}/${inventoryId}/slots`, { params });
  },
  /**
   * `calendar.manage`. A one-off session the weekly template does not cover.
   * `local_date`/`local_time` are a WALL CLOCK in the inventory's own zone —
   * never resolve the instant client-side.
   */
  createSlot: async (kind, inventoryId, data) => {
    return apiClient.post(`/main/api/${slotPath(kind)}/${inventoryId}/slots`, data);
  },
  /**
   * `calendar.manage`. Move or resize one cell. Refuses `409 SLOT_HAS_SEATS`
   * (with `held_count`/`booked_count`) when guests hold seats on it. No override.
   */
  overrideSlot: async (kind, inventoryId, slotId, data) => {
    return apiClient.patch(`/main/api/${slotPath(kind)}/${inventoryId}/slots/${slotId}`, data);
  },
  /**
   * `calendar.manage`. CANCEL, never delete: booking projects a `slot_seats`
   * row keyed on `slot_id`, and a removed row would strand it with no event
   * able to say what happened. `DELETE` is the verb a host means; `cancelled`
   * is what it does, and the row comes back saying so.
   */
  cancelSlot: async (kind, inventoryId, slotId) => {
    return apiClient.delete(`/main/api/${slotPath(kind)}/${inventoryId}/slots/${slotId}`);
  },
  /**
   * The slot picker's data. PUBLIC, like the nightly calendar preview beside it.
   *
   * Every start returned is already proved bookable server-side — cutoff, seat
   * filter and, for a service, the run check that `ceil(duration/granularity)`
   * CONSECUTIVE cells are open. Never re-derive that client-side: a 2-hour shoot
   * that gets 12:00 but not 13:00 is a failed hold, not a shorter shoot.
   *
   * `remaining` is main's MIRROR of booking's counters
   * (`remaining_is_estimate: true`, always) — render "only 2 left", never a
   * promise; the guest who loses the race gets a 409 with the real number.
   */
  getListingSlots: async (listingId, params) => {
    return apiClient.get(`/main/api/listings/${listingId}/slots`, { params });
  },
  /**
   * The guest's experience / service PAGE. Public, same `:listingId` as the read
   * above, because every id a client holds is a listing id.
   *
   * ONE call for both kinds, discriminated by `listing.inventory_kind` — a
   * generated column, so the ROW decides and a caller cannot ask for the wrong
   * arm. Narrow with `isExperienceListingDetail` / `isServiceListingDetail`.
   *
   * 404 covers an unknown id, a STAY listing, an inactive inventory row and a
   * sandbox workspace — ONE answer, so it cannot enumerate listing ids.
   */
  getSlotListingDetail: async (listingId) => {
    return apiClient.get(`/main/api/listings/${listingId}/slot-detail`);
  }
};

export {
  inventorySlotsApi
};
